/**
 * Filter State Store
 * Zustand store for listings filter state management
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ListingFilters, LeadFilters, JobFilters } from '@/types';

type ListingSortState = {
  sortBy: NonNullable<ListingFilters['sortBy']>;
  sortOrder: NonNullable<ListingFilters['sortOrder']>;
};

type LeadSortState = {
  sortBy: NonNullable<LeadFilters['sortBy']>;
  sortOrder: NonNullable<LeadFilters['sortOrder']>;
};

type JobSortState = {
  sortBy: NonNullable<JobFilters['sortBy']>;
  sortOrder: NonNullable<JobFilters['sortOrder']>;
};

// Saved filter view
export interface SavedView {
  id: string;
  name: string;
  filters: ListingFilters;
  sortBy: ListingSortState['sortBy'];
  sortOrder: 'asc' | 'desc';
  createdAt: string;
}

// Active filters state
export interface FilterState {
  // Listings filters
  listingsFilters: ListingFilters;
  setListingsFilters: (filters: ListingFilters) => void;
  updateListingsFilter: <K extends keyof ListingFilters>(
    key: K,
    value: ListingFilters[K]
  ) => void;
  resetListingsFilters: () => void;

  // Leads filters
  leadsFilters: LeadFilters;
  setLeadsFilters: (filters: LeadFilters) => void;
  updateLeadsFilter: <K extends keyof LeadFilters>(
    key: K,
    value: LeadFilters[K]
  ) => void;
  resetLeadsFilters: () => void;

  // Jobs filters
  jobsFilters: JobFilters;
  setJobsFilters: (filters: JobFilters) => void;
  updateJobsFilter: <K extends keyof JobFilters>(
    key: K,
    value: JobFilters[K]
  ) => void;
  resetJobsFilters: () => void;

  // Saved views
  savedViews: SavedView[];
  saveView: (name: string, filters: ListingFilters) => void;
  deleteView: (id: string) => void;
  loadView: (id: string) => ListingFilters | null;

  // Sort preferences
  listingsSort: ListingSortState;
  setListingsSort: (sortBy: ListingSortState['sortBy'], sortOrder: ListingSortState['sortOrder']) => void;

  leadsSort: LeadSortState;
  setLeadsSort: (sortBy: LeadSortState['sortBy'], sortOrder: LeadSortState['sortOrder']) => void;

  jobsSort: JobSortState;
  setJobsSort: (sortBy: JobSortState['sortBy'], sortOrder: JobSortState['sortOrder']) => void;

  // Active filter counts
  getActiveListingsFilterCount: () => number;
  getActiveLeadsFilterCount: () => number;
  getActiveJobsFilterCount: () => number;
}



// Default filters
const defaultListingsFilters: ListingFilters = {
  sortBy: 'privateFirst',
  sortOrder: 'desc',
};

const defaultLeadsFilters: LeadFilters = {
  sortBy: 'createdAt',
  sortOrder: 'desc',
};

const defaultJobsFilters: JobFilters = {
  sortBy: 'createdAt',
  sortOrder: 'desc',
};

// Generate unique view ID
let viewIdCounter = 0;
const generateViewId = () => `view-${++viewIdCounter}-${Date.now()}`;

// Helper to count active filters
function countActiveFilters<T extends Record<string, unknown>>(
  filters: T,
  defaults: T
): number {
  return Object.entries(filters).filter(([key, value]) => {
    if (key === 'sortBy' || key === 'sortOrder') return false;
    const defaultValue = defaults[key as keyof T];
    return value !== undefined && value !== defaultValue && value !== '';
  }).length;
}

export const useFilterStore = create<FilterState>()(
  persist(
    (set, get) => ({
      // Initial State
      listingsFilters: { ...defaultListingsFilters },
      leadsFilters: { ...defaultLeadsFilters },
      jobsFilters: { ...defaultJobsFilters },
      savedViews: [],
      listingsSort: { sortBy: 'privateFirst', sortOrder: 'desc' },
      leadsSort: { sortBy: 'createdAt', sortOrder: 'desc' },
      jobsSort: { sortBy: 'createdAt', sortOrder: 'desc' },

      // Listings Filter Actions
      setListingsFilters: (filters) => set({ listingsFilters: filters }),

      updateListingsFilter: (key, value) =>
        set((state) => ({
          listingsFilters: {
            ...state.listingsFilters,
            [key]: value,
          },
        })),

      resetListingsFilters: () =>
        set({
          listingsFilters: { ...defaultListingsFilters },
        }),

      // Leads Filter Actions
      setLeadsFilters: (filters) => set({ leadsFilters: filters }),

      updateLeadsFilter: (key, value) =>
        set((state) => ({
          leadsFilters: {
            ...state.leadsFilters,
            [key]: value,
          },
        })),

      resetLeadsFilters: () =>
        set({
          leadsFilters: { ...defaultLeadsFilters },
        }),

      // Jobs Filter Actions
      setJobsFilters: (filters) => set({ jobsFilters: filters }),

      updateJobsFilter: (key, value) =>
        set((state) => ({
          jobsFilters: {
            ...state.jobsFilters,
            [key]: value,
          },
        })),

      resetJobsFilters: () =>
        set({
          jobsFilters: { ...defaultJobsFilters },
        }),

      // Saved Views Actions
      saveView: (name, filters) => {
        const newView: SavedView = {
          id: generateViewId(),
          name,
          filters,
          sortBy: get().listingsSort.sortBy,
          sortOrder: get().listingsSort.sortOrder,
          createdAt: new Date().toISOString(),
        };

        set((state) => ({
          savedViews: [...state.savedViews, newView],
        }));
      },

      deleteView: (id) =>
        set((state) => ({
          savedViews: state.savedViews.filter((v) => v.id !== id),
        })),

      loadView: (id) => {
        const view = get().savedViews.find((v) => v.id === id);
        if (view) {
          set({
            listingsFilters: view.filters,
            listingsSort: {
              sortBy: view.sortBy,
              sortOrder: view.sortOrder,
            },
          });
          return view.filters;
        }
        return null;
      },

      // Sort Actions
      setListingsSort: (sortBy, sortOrder) =>
        set({
          listingsSort: { sortBy, sortOrder },
          listingsFilters: {
            ...get().listingsFilters,
            sortBy,
            sortOrder,
          },
        }),

      setLeadsSort: (sortBy, sortOrder) =>
        set({
          leadsSort: { sortBy, sortOrder },
          leadsFilters: {
            ...get().leadsFilters,
            sortBy,
            sortOrder,
          },
        }),

      setJobsSort: (sortBy, sortOrder) =>
        set({
          jobsSort: { sortBy, sortOrder },
          jobsFilters: {
            ...get().jobsFilters,
            sortBy,
            sortOrder,
          },
        }),

      // Active Filter Counts
      getActiveListingsFilterCount: () =>
        countActiveFilters(get().listingsFilters as Record<string, unknown>, defaultListingsFilters as Record<string, unknown>),

      getActiveLeadsFilterCount: () =>
        countActiveFilters(get().leadsFilters as Record<string, unknown>, defaultLeadsFilters as Record<string, unknown>),

      getActiveJobsFilterCount: () =>
        countActiveFilters(get().jobsFilters as Record<string, unknown>, defaultJobsFilters as Record<string, unknown>),
    }),
    {
      name: 'filter-storage',
      partialize: (state) => ({
        savedViews: state.savedViews,
        listingsSort: state.listingsSort,
        leadsSort: state.leadsSort,
        jobsSort: state.jobsSort,
      }),
    }
  )
);

// Filter state selectors
export const selectListingsFilters = (state: FilterState) => state.listingsFilters;
export const selectLeadsFilters = (state: FilterState) => state.leadsFilters;
export const selectJobsFilters = (state: FilterState) => state.jobsFilters;
export const selectSavedViews = (state: FilterState) => state.savedViews;
export const selectListingsSort = (state: FilterState) => state.listingsSort;
export const selectLeadsSort = (state: FilterState) => state.leadsSort;
export const selectJobsSort = (state: FilterState) => state.jobsSort;
